import React, { useEffect, useState, useCallback } from "react";
import "./ProjectSavings.css";
import { supabase } from "../SupabaseClient.tsx";
import InfoBanner from "../components/InfoBanner.tsx";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { PiInfo } from "react-icons/pi";

interface ProjectionDataPoint {
  age: number;
  withDelay: number;
  withoutDelay: number;
}

const ProjectSavings = () => {
  // Form inputs
  const [currentAge, setCurrentAge] = useState<number | string>("");
  const [retirementAge, setRetirementAge] = useState<number | string>("");
  const [currentBalance, setCurrentBalance] = useState<number | string>("");
  const [annualMedicalExpenses, setAnnualMedicalExpenses] = useState<
    number | string
  >("");
  const [totalHSAContribution, setTotalHSAContribution] = useState<
    number | string
  >("");
  const [cagr, setCAGR] = useState<number | string>(8);
  const [loading, setLoading] = useState<boolean>(true);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Banner visibility
  const [showInfoBanner, setShowInfoBanner] = useState<boolean>(true);

  // Chart data
  const [chartData, setChartData] = useState<ProjectionDataPoint[]>([]);
  const [showChart, setShowChart] = useState<boolean>(false);

  // Define calculateProjections with useCallback
  const calculateProjections = useCallback(() => {
    // Validate inputs
    const currAge = Number(currentAge);
    const retAge = Number(retirementAge);
    const balance = Number(currentBalance);
    const medicalExp = Number(annualMedicalExpenses);
    const hsaContrib = Number(totalHSAContribution);
    const cagrRate = Number(cagr);

    // Check if all required fields are filled
    if (
      currentAge === "" ||
      retirementAge === "" ||
      currentBalance === "" ||
      annualMedicalExpenses === "" ||
      totalHSAContribution === "" ||
      cagr === ""
    ) {
      // Not all fields filled yet, don't show error
      return;
    }

    if (
      !currAge ||
      !retAge ||
      currAge >= retAge ||
      balance < 0 ||
      medicalExp < 0 ||
      hsaContrib < 0 ||
      !cagrRate
    ) {
      setError(
        "Please enter valid values. Retirement age must be greater than current age."
      );
      return;
    }

    // Clear any previous error
    setError(null);

    const years = retAge - currAge;
    const projections: ProjectionDataPoint[] = [];
    const rate = cagrRate / 100;

    // Calculate year by year
    for (let year = 0; year <= years; year++) {
      const age = currAge + year;

      // Scenario 1: WITH delayed reimbursement (medical expenses stay invested)
      let balanceWithDelay = balance;
      for (let i = 0; i < year; i++) {
        // Add contributions
        balanceWithDelay += hsaContrib;
        // Grow the balance (including medical expenses that weren't withdrawn)
        balanceWithDelay *= 1 + rate;
      }

      // Scenario 2: WITHOUT delayed reimbursement (medical expenses withdrawn immediately)
      let balanceWithoutDelay = balance;
      for (let i = 0; i < year; i++) {
        // Add contributions
        balanceWithoutDelay += hsaContrib;
        // Withdraw medical expenses immediately
        balanceWithoutDelay -= medicalExp;
        // Grow the remaining balance
        balanceWithoutDelay *= 1 + rate;
      }

      projections.push({
        age,
        withDelay: Math.round(balanceWithDelay),
        withoutDelay: Math.max(0, Math.round(balanceWithoutDelay)), // Ensure non-negative
      });
    }

    setChartData(projections);
    setShowChart(true);
  }, [
    currentAge,
    retirementAge,
    currentBalance,
    annualMedicalExpenses,
    totalHSAContribution,
    cagr,
  ]);

  // Load CAGR from settings and restore form values from sessionStorage
  useEffect(() => {
    const fetchCAGR = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("users")
          .select("interest_rate")
          .single();

        if (error) {
          console.error("Error fetching interest rate:", error);
          setCAGR(8); // Default to 8% if error
        } else {
          setCAGR(data.interest_rate || 8);
        }
      } catch (error) {
        console.error("Unexpected error fetching CAGR:", error);
        setCAGR(8);
      } finally {
        setLoading(false);
      }
    };

    fetchCAGR();

    // Restore banner visibility from sessionStorage
    const bannerDismissed = sessionStorage.getItem(
      "projectSavings_infoBannerDismissed"
    );
    if (bannerDismissed === "true") {
      setShowInfoBanner(false);
    }

    // Restore form values from sessionStorage
    const savedCurrentAge = sessionStorage.getItem("projectSavings_currentAge");
    const savedRetirementAge = sessionStorage.getItem(
      "projectSavings_retirementAge"
    );
    const savedCurrentBalance = sessionStorage.getItem(
      "projectSavings_currentBalance"
    );
    const savedAnnualMedicalExpenses = sessionStorage.getItem(
      "projectSavings_annualMedicalExpenses"
    );
    const savedTotalHSAContribution = sessionStorage.getItem(
      "projectSavings_totalHSAContribution"
    );

    if (savedCurrentAge) setCurrentAge(Number(savedCurrentAge));
    if (savedRetirementAge) setRetirementAge(Number(savedRetirementAge));
    if (savedCurrentBalance) setCurrentBalance(Number(savedCurrentBalance));
    if (savedAnnualMedicalExpenses)
      setAnnualMedicalExpenses(Number(savedAnnualMedicalExpenses));
    if (savedTotalHSAContribution)
      setTotalHSAContribution(Number(savedTotalHSAContribution));
  }, []);

  // Auto-calculate only once when CAGR is loaded and we have saved values from sessionStorage
  useEffect(() => {
    if (!loading) {
      // Only auto-calculate if we have all values restored from session
      const savedCurrentAge = sessionStorage.getItem(
        "projectSavings_currentAge"
      );
      const savedRetirementAge = sessionStorage.getItem(
        "projectSavings_retirementAge"
      );
      const savedCurrentBalance = sessionStorage.getItem(
        "projectSavings_currentBalance"
      );
      const savedAnnualMedicalExpenses = sessionStorage.getItem(
        "projectSavings_annualMedicalExpenses"
      );
      const savedTotalHSAContribution = sessionStorage.getItem(
        "projectSavings_totalHSAContribution"
      );

      if (
        savedCurrentAge &&
        savedRetirementAge &&
        savedCurrentBalance &&
        savedAnnualMedicalExpenses &&
        savedTotalHSAContribution
      ) {
        calculateProjections();
      }
    }
  }, [loading, calculateProjections]);

  // Save to sessionStorage whenever values change
  useEffect(() => {
    if (currentAge !== "")
      sessionStorage.setItem("projectSavings_currentAge", String(currentAge));
    if (retirementAge !== "")
      sessionStorage.setItem(
        "projectSavings_retirementAge",
        String(retirementAge)
      );
    if (currentBalance !== "")
      sessionStorage.setItem(
        "projectSavings_currentBalance",
        String(currentBalance)
      );
    if (annualMedicalExpenses !== "")
      sessionStorage.setItem(
        "projectSavings_annualMedicalExpenses",
        String(annualMedicalExpenses)
      );
    if (totalHSAContribution !== "")
      sessionStorage.setItem(
        "projectSavings_totalHSAContribution",
        String(totalHSAContribution)
      );
  }, [
    currentAge,
    retirementAge,
    currentBalance,
    annualMedicalExpenses,
    totalHSAContribution,
  ]);

  const handleInputChange = (
    setter: React.Dispatch<React.SetStateAction<number | string>>,
    value: string
  ) => {
    if (value === "") {
      setter("");
    } else {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        setter(num);
      }
    }
  };

  // Handle blur - validate and calculate when user clicks out of input
  const handleInputBlur = () => {
    calculateProjections();
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const difference = payload[0].value - payload[1].value;
      return (
        <div className="projection-tooltip">
          <p className="projection-tooltip-age">Age {payload[0].payload.age}</p>
          <div className="projection-tooltip-row">
            <span className="tooltip-label with-delay">With Delay:</span>
            <span className="tooltip-value">
              $
              {payload[0].value
                .toFixed(0)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </span>
          </div>
          <div className="projection-tooltip-row">
            <span className="tooltip-label without-delay">Without Delay:</span>
            <span className="tooltip-value">
              $
              {payload[1].value
                .toFixed(0)
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </span>
          </div>
          <div className="projection-tooltip-difference">
            <span>Difference:</span>
            <span className="difference-value">
              ${difference.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  const handleDismissBanner = () => {
    setShowInfoBanner(false);
    sessionStorage.setItem("projectSavings_infoBannerDismissed", "true");
  };

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Project Savings</h1>
            <p>
              Model the impact of delayed reimbursement on your HSA balance at
              retirement.
            </p>
          </div>
        </div>

        {showInfoBanner && (
          <InfoBanner
            icon={<PiInfo size={24} />}
            color="blue"
            ctaText="Dismiss"
            onCtaClick={handleDismissBanner}
          >
            <p>
              Your projection data is stored locally in your browser and is not
              saved to our servers.
            </p>
          </InfoBanner>
        )}

        {/* Input Form */}
        <div className="projection-inputs">
          {error && <div className="projection-error">{error}</div>}
          <div className="input-row">
            <div className="input-group">
              <label htmlFor="currentAge">Current Age</label>
              <div className="input-with-unit">
                <input
                  id="currentAge"
                  type="number"
                  value={currentAge}
                  onChange={(e) =>
                    handleInputChange(setCurrentAge, e.target.value)
                  }
                  onBlur={handleInputBlur}
                  placeholder="30"
                  min="18"
                  max="100"
                />
                <span className="input-unit">years</span>
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="retirementAge">Retirement Age</label>
              <div className="input-with-unit">
                <input
                  id="retirementAge"
                  type="number"
                  value={retirementAge}
                  onChange={(e) =>
                    handleInputChange(setRetirementAge, e.target.value)
                  }
                  onBlur={handleInputBlur}
                  placeholder="65"
                  min="18"
                  max="100"
                />
                <span className="input-unit">years</span>
              </div>
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label htmlFor="currentBalance">Current HSA Balance</label>
              <div className="input-with-unit">
                <span className="input-unit-prefix">$</span>
                <input
                  id="currentBalance"
                  type="number"
                  value={currentBalance}
                  onChange={(e) =>
                    handleInputChange(setCurrentBalance, e.target.value)
                  }
                  onBlur={handleInputBlur}
                  placeholder="5000"
                  min="0"
                  className="with-prefix"
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="annualMedicalExpenses">
                Annual Medical Expenses
              </label>
              <div className="input-with-unit">
                <span className="input-unit-prefix">$</span>
                <input
                  id="annualMedicalExpenses"
                  type="number"
                  value={annualMedicalExpenses}
                  onChange={(e) =>
                    handleInputChange(setAnnualMedicalExpenses, e.target.value)
                  }
                  onBlur={handleInputBlur}
                  placeholder="2000"
                  min="0"
                  className="with-prefix"
                />
              </div>
            </div>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label htmlFor="totalHSAContribution">
                Total Annual HSA Contribution
              </label>
              <div className="input-with-unit">
                <span className="input-unit-prefix">$</span>
                <input
                  id="totalHSAContribution"
                  type="number"
                  value={totalHSAContribution}
                  onChange={(e) =>
                    handleInputChange(setTotalHSAContribution, e.target.value)
                  }
                  onBlur={handleInputBlur}
                  placeholder="4000"
                  min="0"
                  className="with-prefix"
                />
              </div>
              <p className="input-helper-text">
                Combined personal and employer contributions
              </p>
            </div>

            <div className="input-group">
              <label htmlFor="cagr">
                CAGR (Compound Annual Growth Rate)
                {loading && <span className="loading-text"> Loading...</span>}
              </label>
              <div className="input-with-unit">
                <input
                  id="cagr"
                  type="number"
                  value={cagr}
                  onChange={(e) => handleInputChange(setCAGR, e.target.value)}
                  placeholder="8"
                  min="0"
                  max="100"
                  step="0.1"
                  disabled={true}
                />
                <span className="input-unit">%</span>
              </div>
              <p className="input-helper-text">
                From your <a href="/settings">Settings</a> page
              </p>
            </div>
          </div>
        </div>

        {/* Chart Display */}
        {showChart && chartData.length > 0 && (
          <div className="projection-chart">
            <h2 className="container-label-header">
              HSA Balance Projection to Retirement
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--color-grey-200)"
                />
                <XAxis
                  dataKey="age"
                  label={{
                    value: "Age",
                    position: "insideBottom",
                    offset: -10,
                  }}
                  tick={{ fill: "var(--color-grey-600)", fontSize: 12 }}
                  tickMargin={10}
                  axisLine={{ stroke: "var(--color-grey-300)" }}
                />
                <YAxis
                  tickFormatter={(value) =>
                    `$${value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                  }
                  tick={{ fill: "var(--color-grey-600)", fontSize: 12 }}
                  axisLine={{ stroke: "var(--color-grey-300)" }}
                  tickMargin={10}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  height={36}
                  iconType="line"
                  wrapperStyle={{ paddingBottom: "20px" }}
                />
                <Line
                  type="monotone"
                  dataKey="withDelay"
                  stroke="var(--color-green-600)"
                  strokeWidth={3}
                  dot={false}
                  name="With Delayed Reimbursement"
                  activeDot={{ r: 6, fill: "var(--color-green-700)" }}
                />
                <Line
                  type="monotone"
                  dataKey="withoutDelay"
                  stroke="var(--color-red-500)"
                  strokeWidth={3}
                  dot={false}
                  name="Without Delayed Reimbursement"
                  activeDot={{ r: 6, fill: "var(--color-red-700)" }}
                />
              </LineChart>
            </ResponsiveContainer>

            {/* Summary Stats */}
            <div className="projection-summary">
              <div className="summary-card">
                <h3>With Delayed Reimbursement</h3>
                <p className="summary-value with-delay">
                  $
                  {chartData[chartData.length - 1].withDelay
                    .toFixed(0)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                </p>
                <p className="summary-label">Balance at Retirement</p>
              </div>

              <div className="summary-card">
                <h3>Without Delayed Reimbursement</h3>
                <p className="summary-value without-delay">
                  $
                  {chartData[chartData.length - 1].withoutDelay
                    .toFixed(0)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                </p>
                <p className="summary-label">Balance at Retirement</p>
              </div>

              <div className="summary-card highlight">
                <h3>Additional Savings</h3>
                <p className="summary-value difference">
                  $
                  {(
                    chartData[chartData.length - 1].withDelay -
                    chartData[chartData.length - 1].withoutDelay
                  )
                    .toFixed(0)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                </p>
                <p className="summary-label">
                  By delaying reimbursements until retirement
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectSavings;
